/**
 * @file Helpers de seleção e filtragem de países do EP.
 * @module components/create/geography/options
 *
 * `filterCountryOptions()` é a referência das opções `accept`, `reject` e
 * `continentGroup`; `configureCountryCombo()` acrescenta as opções de bloqueio.
 * Consumidores devem apontar para essas funções com `@see`, sem criar nomes de
 * tipos paralelos.
 */

const normalizeToken = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .replace(/[^a-zA-Z0-9]+/g, '')
  .toLowerCase();

const REGION_ALIASES = Object.freeze({
  africa: 'africa',
  asia: 'asia',
  europe: 'europe',
  europa: 'europe',
  oceania: 'oceania',
  northamerica: 'northamerica',
  americadonorte: 'northamerica',
  americadelnorte: 'northamerica',
  centralamerica: 'centralamerica',
  americacentral: 'centralamerica',
  southamerica: 'southamerica',
  americadosul: 'southamerica',
  americadelsur: 'southamerica',
});

const normalizeRegion = (value) => {
  const token = normalizeToken(value);
  return REGION_ALIASES[token] || token;
};

const normalizeFilter = (value) => {
  const source = Array.isArray(value)
    ? value
    : (value === undefined || value === null || value === '' ? [] : [value]);

  return [...new Set(source.map(normalizeRegion).filter(Boolean))];
};

const rawOptionValue = (option) => String(
  option && typeof option === 'object'
    ? option.value ?? option.iso2 ?? option.acronym ?? option.code ?? ''
    : option ?? ''
).trim();

const optionCode = (option) => normalizeToken(rawOptionValue(option));

const optionRegion = (option) => {
  if (!option || typeof option !== 'object') return '';

  const candidates = [
    option.continentKey,
    option.regionKey,
    option.continent,
    option.region,
    option.group?.key,
    option.group?.label,
    option.group?.text,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeRegion(candidate);
    if (normalized && REGION_ALIASES[normalized]) return REGION_ALIASES[normalized];
    if (normalized && ['africa', 'asia', 'europe', 'oceania', 'northamerica', 'centralamerica', 'southamerica'].includes(normalized)) {
      return normalized;
    }
  }

  return '';
};

/**
 * Retorna a chave canônica de continente/região de uma opção de país.
 *
 * Chaves vigentes: `africa`, `asia`, `europe`, `oceania`, `northamerica`,
 * `centralamerica` e `southamerica`. Não existe uma chave genérica `americas`.
 *
 * @param {Object} option Opção de país.
 * @returns {string} Chave normalizada ou string vazia.
 */
export const countryContinentKey = (option) => optionRegion(option);

/**
 * Retorna o label localizado do continente/região de uma opção.
 *
 * @param {Object} option Opção de país.
 * @param {Object} [dictionary=globalThis.dic] Dicionário EP usado para localização.
 * @returns {string} Label localizado ou melhor fallback disponível.
 * @see countryContinentKey
 */
export const countryContinentLabel = (option, dictionary = globalThis?.dic) => {
  const key = countryContinentKey(option);
  const label = dictionary?.labels?.continents?.[key];

  return String(label || option?.continent || option?.group?.label || option?.group?.text || key || '').trim();
};

const matchesToken = (option, token) => {
  if (!token) return false;
  return optionCode(option) === token || optionRegion(option) === token;
};

/**
 * Filtra opções de país.
 *
 * `accept` é aplicado antes de `reject`. Os valores podem ser códigos de país
 * ou chaves/aliases de região. Para países, use preferencialmente ISO 3166-1
 * alpha-2, por exemplo `BR` e `US`. Quando `continentGroup === false`, os
 * objetos retornados são clonados sem a propriedade `group`.
 *
 * @param {Array<*>} [items=[]] Opções de país.
 * @param {Object} [options={}] Filtros e apresentação.
 * @param {string|string[]} [options.accept] Países ou regiões permitidos.
 * @param {string|string[]} [options.reject] Países ou regiões excluídos.
 * @param {boolean} [options.continentGroup=true] Quando `false`, remove os grupos de continente/região.
 * @returns {Array<*>} Opções filtradas; o array original não é mutado.
 */
export const filterCountryOptions = (items = [], options = {}) => {
  const source = Array.isArray(items) ? items : [];
  const accept = normalizeFilter(options.accept);
  const reject = normalizeFilter(options.reject);

  let filtered = accept.length > 0
    ? source.filter((option) => accept.some((token) => matchesToken(option, token)))
    : [...source];

  if (reject.length > 0) {
    filtered = filtered.filter((option) => !reject.some((token) => matchesToken(option, token)));
  }

  if (options.continentGroup === false) {
    filtered = filtered.map((option) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) return option;
      const next = { ...option };
      delete next.group;
      return next;
    });
  }

  return filtered;
};

/**
 * Bloqueia ou libera a interação de um Combo de país já criado.
 *
 * @param {Object} combo Instância runtime do Combo EP.
 * @param {boolean} [locked=true] Estado de bloqueio.
 * @returns {boolean} `true` quando o combo ficou bloqueado.
 */
export const setCountryComboLocked = (combo, locked = true) => {
  if (!combo?.elem) return false;

  const elem = combo.elem;
  const currentHandler = elem.__countryOptionsLockHandler;

  if (currentHandler) {
    elem.removeEventListener('click', currentHandler, true);
    delete elem.__countryOptionsLockHandler;
  }

  elem.classList.toggle('country-locked', locked === true);
  if (locked === true) {
    elem.setAttribute('aria-disabled', 'true');
    combo.selected?.setAttribute?.('aria-disabled', 'true');
  } else {
    elem.removeAttribute('aria-disabled');
    combo.selected?.removeAttribute?.('aria-disabled');
  }

  if (locked !== true) return false;

  const handler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  };

  elem.__countryOptionsLockHandler = handler;
  elem.addEventListener('click', handler, true);
  combo.closeOptions?.();
  return true;
};

/**
 * Aplica filtro, seleção preferencial e bloqueio a um Combo de país.
 *
 * A primeira chamada preserva a lista original em `combo.__countryOptionsSource`.
 * Se o valor atual continuar permitido ele é mantido. Se restar uma única
 * opção, ela é selecionada e o combo é bloqueado.
 *
 * @param {Object} combo Instância runtime do Combo EP.
 * @param {Object} [options={}] Configuração de países.
 * @param {string|string[]} [options.accept] Países ou regiões permitidos; veja {@link filterCountryOptions}.
 * @param {string|string[]} [options.reject] Países ou regiões excluídos; veja {@link filterCountryOptions}.
 * @param {boolean} [options.continentGroup=true] Agrupamento por continente/região.
 * @param {boolean} [options.readOnly=false] Bloqueia a interação sem alterar o valor.
 * @param {boolean} [options.readonly] Alias de compatibilidade para `readOnly`.
 * @param {boolean} [options.disabled=false] Bloqueia a interação.
 * @returns {{options:Array<*>,locked:boolean,value:string}} Estado aplicado.
 * @throws {Error} Quando a origem possui países e `accept`/`reject` remove todos.
 * @see filterCountryOptions
 * @see setCountryComboLocked
 */
export const configureCountryCombo = (combo, options = {}) => {
  if (!combo?.renderData) {
    return { options: [], locked: false, value: '' };
  }

  if (!Array.isArray(combo.__countryOptionsSource)) {
    combo.__countryOptionsSource = Array.isArray(combo.data) ? [...combo.data] : [];
  }

  const source = combo.__countryOptionsSource;
  const filtered = filterCountryOptions(source, options);

  if (source.length > 0 && filtered.length === 0) {
    throw new Error('Country options: accept/reject não deixou nenhum país disponível.');
  }

  const currentValue = String(combo.hidden?.value ?? combo.options?.value ?? '').trim();
  const currentAllowed = filtered.some((option) => optionCode(option) === normalizeToken(currentValue));
  const onlyValue = filtered.length === 1 ? rawOptionValue(filtered[0]) : '';
  const preferredValue = currentAllowed ? currentValue : (onlyValue || null);

  combo.renderData(filtered, preferredValue);

  const explicitLock = (
    options.disabled === true
    || options.readOnly === true
    || options.readonly === true
  );
  const locked = explicitLock || filtered.length === 1;
  setCountryComboLocked(combo, locked);

  return {
    options: filtered,
    locked,
    value: String(combo.hidden?.value ?? ''),
  };
};

/**
 * Normaliza código de país, região ou alias para comparação interna.
 *
 * @param {*} value Valor de entrada.
 * @returns {string} Token sem acentos, espaços ou pontuação, em minúsculas.
 */
export const normalizeCountryOptionToken = normalizeToken;
